import { Link } from "react-router-dom"
import { useContext, useState, useEffect } from "react"
import { DateTime } from "luxon"

import { useQuery } from "@apollo/client"

import ParticipantSidebar from "../../components/sidebars/ParticipantSidebar"
import ParticipantProfileBoxSmall from "../../components/ParticipantProfileBoxSmall"

import styles from "../../styles/CustomDataForms.module.css"

// Loader
import LoaderMedium from "../../components/Loaders"

// Context
import { UserContext } from "../../context/userContext"

// helpers
import dataParser from "../../helpers/parseJsonData"

// queries
import GLOBALS_STATUS from "../../config/globalsStatus"
import _ from "lodash"
import { CUSTOM_FORM_BY_PARTICIPANT } from "./queries"

//Hooks
import useLabels from "../../hooks/useLabels"

export default function HepCForm() {
  const [loading, setLoading] = useState(true)
  const [noForm, setNoForm] = useState(false)
  const [data, setData] = useState(null)
  const userCtx = useContext(UserContext)
  const [loadingLabels, labels] = useLabels(["hep_c_form"])

  const ID_TEMPLATE = "hep_c_form"

  const { refetch } = useQuery(CUSTOM_FORM_BY_PARTICIPANT, {
    variables: {
      participant_id: userCtx.participant_id,
      organization_id: userCtx.session.user.organization_id,
      participant_membership_id: userCtx.participant_membership_id,
      idTemplate: ID_TEMPLATE,
    },
    skip: !userCtx,
    onCompleted: async (data) => {
      if (data.customForm === null) {
        setNoForm(true)
        return setLoading(false)
      }
      const dataFormatted = dataParser(data.customForm.data)
      setData(dataFormatted[0])
      setLoading(false)
    },
  })

  useEffect(() => {
    setLoading(true)
    setData(null)
    setNoForm(false)
    refetch()
  }, [userCtx.current_participant_id, refetch])

  if (loading || loadingLabels) return <LoaderMedium />

  const handleData = () => {
    if (noForm) {
      return (
        <div>
          <h2>No {labels.hep_c_form} found for current Participant.</h2>
          <ul className={styles.cta}>
            <li>
              {_.includes(
                GLOBALS_STATUS.ACTIVE_STATUS,
                userCtx.participant_membership_status
              ) ? (
                <Link to={`/form/${ID_TEMPLATE}/1/`} className="btn">
                  New {labels.hep_c_form}
                </Link>
              ) : (
                ""
              )}
            </li>
          </ul>
        </div>
      )
    }
    return (
      <div>
        <div>
          <ul className={styles.cta}>
            <li>
              {_.includes(
                GLOBALS_STATUS.ACTIVE_STATUS,
                userCtx.participant_membership_status
              ) ? (
                <Link
                  to={`/form/${ID_TEMPLATE}/1/${data.idStamp}`}
                  className="btn"
                >
                  Update {labels.hep_c_form}
                </Link>
              ) : (
                ""
              )}
            </li>
          </ul>
        </div>
        <br />
        <div className={styles.table}>
          <div className={styles.tableRow}>
            <div>Form Date: </div>
            <div>{DateTime.fromISO(data.form_date).toISODate()}</div>
          </div>
          <div className={styles.tableRow}>
            <div>
              Has someone told you that you currently have chronic (long-term)
              hepatitis C?:
            </div>
            <div>{data.hep_c_chronic}</div>
          </div>
          <div className={styles.tableRow}>
            <div>HCV antibody test date: </div>
            <div>{DateTime.fromISO(data.hep_test_c_date).toISODate()}</div>
          </div>
          <div className={styles.tableRow}>
            <div>Was the HCV test conducted as a Rapid HCV antibody test?</div>
            <div>{data.hep_test_c_anti_HCV}</div>
          </div>
          <div className={styles.tableRow}>
            <div>HCV antibody test result: </div>
            <div>{data.hep_c_anti_HCV_result}</div>
          </div>
          <div className={styles.tableRow}>
            <div>
              Was person referred for HCV RNA Viral Load to confirm chronic
              (on-going) HCV infection?
            </div>
            <div>{data.hep_hcv_antibody_reactive_referred}</div>
          </div>
          <div className={styles.tableRow}>
            <div>HCV RNA test date</div>
            <div>
              {DateTime.fromISO(data.hep_test_hcv_rna_date).toISODate()}
            </div>
          </div>
          <div className={styles.tableRow}>
            <div>HCV RNA test result</div> <div>{data.hep_hcv_rna_result}</div>
          </div>
          <div className={styles.tableRow}>
            <div>How was the person linked to care?</div>
            <div>{data.hep_hcv_rna_care_link}</div>
          </div>
          <div className={styles.tableRow}>
            <div>Person received HCV curative treatment?</div>
            <div>{data.hep_hcv_treatment}</div>
          </div>
          <div className={styles.tableRow}>
            <div>HCV medication date pick up</div>
            <div>
              {DateTime.fromISO(data.hep_hcv_medication_date).toISODate()}
            </div>
          </div>
          <div className={styles.tableRow}>
            <div>Person completed HCV treatment course</div>
            <div>{data.hep_hcv_treatment_complete}</div>
          </div>
          <div className={styles.tableRow}>
            <div>
              HCV RNA Viral Load test result after completing treatment for
              SVR/Cure
            </div>
            <div>{data.hep_hcv_rna_post_test_result}</div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="layout-with-sidebar">
      <div className="sidebar">
        <ParticipantSidebar />
      </div>
      <div className="main-content">
        <ParticipantProfileBoxSmall />
        <h1 className={styles.title}>{labels.hep_c_form}</h1>
        <div className={styles.contentWrapper}>{handleData()}</div>
      </div>
    </div>
  )
}
